<template>
  <v-container fluid class="admin-page">
    <v-row class="align-center" justify="space-between">
      <v-col cols="6">
        <h2>Gift Rules</h2>
      </v-col>
      <v-col cols="6">
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="searchQuery"
              label="Search Gift Rule"
              append-icon="mdi-magnify"
              solo
              clearable
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-btn
              class="text--futura text--white text-capitalize"
              dark
              block
              @click="startCreate"
            >
              {{ isEditing ? "Create New Rule" : "Add Gift Rule" }}
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <Alert />

    <v-row class="crystal-admin-form justify-space-around">
      <v-col cols="5">
        <h3>{{ isEditing ? "Edit Gift Rule" : "Create Gift Rule" }}</h3>
        <label>Rule Name</label>
        <v-text-field
          solo
          placeholder="e.g. Buy Anchovy, Get Sambal"
          v-model="form.name"
        ></v-text-field>

        <label>Trigger Type</label>
        <v-radio-group v-model="form.triggerType" row>
          <v-radio label="Buy Item A" value="item"></v-radio>
          <v-radio label="Spend X Amount" value="minTransaction"></v-radio>
        </v-radio-group>

        <template v-if="form.triggerType === 'item'">
          <label>Trigger Items</label>
          <small>Choose one or more items that unlock the free gift.</small>
          <v-autocomplete
            class="mt-2"
            solo
            multiple
            chips
            deletable-chips
            clearable
            :items="products"
            item-text="name"
            item-value="id"
            label="Choose Trigger Items"
            v-model="form.triggerItemIds"
          ></v-autocomplete>
        </template>

        <template v-else>
          <label>Minimum Transaction</label>
          <v-text-field
            solo
            type="number"
            prefix="Rp | "
            placeholder="Enter minimum spend"
            v-model="form.minTransaction"
          ></v-text-field>
        </template>
      </v-col>

      <v-col cols="5">
        <label>Gift Item</label>
        <v-autocomplete
          solo
          clearable
          :items="products"
          item-text="name"
          item-value="id"
          label="Choose Gift Item"
          v-model="form.giftItemId"
        ></v-autocomplete>

        <label>Gift Quantity</label>
        <v-text-field
          solo
          type="number"
          min="1"
          placeholder="Enter gift quantity"
          v-model="form.giftQty"
        ></v-text-field>

        <label>Status</label>
        <v-switch
          :label="form.isActive ? 'Active' : 'Inactive'"
          v-model="form.isActive"
          color="success"
        ></v-switch>

        <v-row class="mt-4">
          <v-col cols="6">
            <v-btn
              dark
              block
              class="text--futura text--white text-capitalize"
              :loading="buttonLoading"
              :disabled="buttonLoading"
              @click="saveGift"
            >
              {{ isEditing ? "Update Rule" : "Save Rule" }}
            </v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn block class="text-capitalize" @click="resetForm">
              Cancel
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="filteredGifts"
          :items-per-page="15"
          class="elevation-1"
        >
          <template v-slot:[`item.triggerType`]="{ item }">
            <span>{{ item.triggerType === "item" ? "Buy Item" : "Min Transaction" }}</span>
          </template>
          <template v-slot:[`item.condition`]="{ item }">
            <span>{{ giftCondition(item) }}</span>
          </template>
          <template v-slot:[`item.giftItem`]="{ item }">
            <span>{{ item.giftItem ? item.giftItem.name : "-" }}</span>
          </template>
          <template v-slot:[`item.isActive`]="{ item }">
            <v-switch
              v-model="item.isActive"
              @click="toggleStatus(item)"
            ></v-switch>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-icon small class="mr-2" @click="editGift(item)">mdi-pencil</v-icon>
            <v-icon small @click="showDeleteDialog(item)">mdi-delete</v-icon>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <DeleteDialog
      :message="deleteMessage"
      :item="selectedGift"
      :showDelete="showDelete"
      @deleteItem="deleteGift"
      @closeDialog="closeDialog"
    />
  </v-container>
</template>

<script>
import Alert from "@/components/Alert.vue";
import DeleteDialog from "@/components/DeleteDialog.vue";
import server from "../api";

const initialForm = () => ({
  id: null,
  name: "",
  triggerType: "item",
  triggerItemIds: [],
  minTransaction: null,
  giftItemId: null,
  giftQty: 1,
  isActive: true,
});

export default {
  name: "Gift",
  components: {
    Alert,
    DeleteDialog,
  },
  data() {
    return {
      gifts: [],
      form: initialForm(),
      selectedGift: {},
      searchQuery: "",
      showDelete: false,
      buttonLoading: false,
      headers: [
        { text: "Name", value: "name" },
        { text: "Trigger Type", value: "triggerType" },
        { text: "Condition", value: "condition" },
        { text: "Gift Item", value: "giftItem" },
        { text: "Gift Qty", value: "giftQty" },
        { text: "Status", value: "isActive" },
        { text: "Action", value: "actions", sortable: false },
      ],
    };
  },
  created() {
    this.$store.dispatch("getProductsLite");
    this.fetchGifts();
  },
  computed: {
    isEditing() {
      return !!this.form.id;
    },
    products() {
      return this.$store.state.select_products;
    },
    deleteMessage() {
      return `Remove Gift Rule ${this.selectedGift.name || ""} ?`;
    },
    filteredGifts() {
      const query = (this.searchQuery || "").toLowerCase();

      if (!query) {
        return this.gifts;
      }

      return this.gifts.filter((gift) => {
        const giftItemName = gift.giftItem ? gift.giftItem.name.toLowerCase() : "";
        return (
          (gift.name || "").toLowerCase().includes(query) ||
          giftItemName.includes(query)
        );
      });
    },
  },
  methods: {
    normalizeIdArray(values) {
      const baseValues = Array.isArray(values)
        ? values
        : values != null && values !== ""
        ? [values]
        : [];

      return [...new Set(
        baseValues
          .map((value) => {
            if (typeof value === "string" && value.includes(",")) {
              return value.split(",");
            }
            return value;
          })
          .flat()
          .map((value) => {
            if (
              typeof value === "string" &&
              value.startsWith("[") &&
              value.endsWith("]")
            ) {
              try {
                return JSON.parse(value);
              } catch (error) {
                return [];
              }
            }
            return value;
          })
          .flat()
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value > 0),
      )];
    },
    async fetchGifts() {
      try {
        const { data } = await server.get("/gifts", {
          headers: {
            token: localStorage.token,
          },
        });
        this.gifts = data.Gifts.map((gift) => ({
          ...gift,
          triggerItemIds: this.normalizeIdArray(gift.triggerItemIds),
        }));
      } catch (err) {
        this.$store.commit("SET_ERROR", err.response?.data?.errors?.message || err.message);
      }
    },
    startCreate() {
      this.resetForm();
    },
    resetForm() {
      this.form = initialForm();
    },
    buildPayload() {
      return {
        name: this.form.name,
        triggerType: this.form.triggerType,
        triggerItemIds:
          this.form.triggerType === "item"
            ? this.normalizeIdArray(this.form.triggerItemIds)
            : [],
        minTransaction:
          this.form.triggerType === "minTransaction" && this.form.minTransaction
            ? Number(this.form.minTransaction)
            : null,
        giftItemId: this.form.giftItemId,
        giftQty: this.form.giftQty || 1,
        isActive: this.form.isActive,
      };
    },
    async saveGift() {
      this.buttonLoading = true;

      try {
        const payload = this.buildPayload();
        const request = this.isEditing
          ? server.put(`/gifts/${this.form.id}`, payload, {
              headers: { token: localStorage.token },
            })
          : server.post("/gifts", payload, {
              headers: { token: localStorage.token },
            });

        const { data } = await request;
        this.$store.commit(
          "SET_SUCCESS",
          `${this.isEditing ? "Updated" : "Created"} gift rule ${data.Gift.name}`
        );
        this.resetForm();
        await this.fetchGifts();
      } catch (err) {
        this.$store.commit(
          "SET_ERROR",
          err.response?.data?.errors?.message || err.response?.data?.message || err.message
        );
      } finally {
        this.buttonLoading = false;
      }
    },
    editGift(gift) {
      this.form = {
        id: gift.id,
        name: gift.name,
        triggerType: gift.triggerType,
        triggerItemIds: this.normalizeIdArray(gift.triggerItemIds),
        minTransaction: gift.minTransaction,
        giftItemId: gift.giftItemId,
        giftQty: gift.giftQty || 1,
        isActive: !!gift.isActive,
      };
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    giftCondition(gift) {
      if (gift.triggerType === "item") {
        const itemNames = this.products
          .filter((product) => this.normalizeIdArray(gift.triggerItemIds).includes(product.id))
          .map((product) => product.name);
        return itemNames.length ? `Buy: ${itemNames.join(", ")}` : "Buy selected item(s)";
      }

      return `Min. transaction Rp ${Number(gift.minTransaction || 0).toLocaleString("id-ID")}`;
    },
    async toggleStatus(item) {
      const nextStatus = !!item.isActive;

      try {
        const { data } = await server.put(
          `/gifts/${item.id}`,
          { isActive: nextStatus },
          {
            headers: {
              token: localStorage.token,
            },
          }
        );
        this.$store.commit(
          "SET_SUCCESS",
          `Gift rule ${data.Gift.name} is now ${data.Gift.isActive ? "Active" : "Inactive"}`
        );
        await this.fetchGifts();
      } catch (err) {
        item.isActive = !nextStatus;
        this.$store.commit(
          "SET_ERROR",
          err.response?.data?.errors?.message || err.response?.data?.message || err.message
        );
      }
    },
    showDeleteDialog(gift) {
      this.selectedGift = gift;
      this.showDelete = true;
    },
    closeDialog() {
      this.showDelete = false;
    },
    async deleteGift(id) {
      try {
        const { data } = await server.delete(`/gifts/${id}`, {
          headers: {
            token: localStorage.token,
          },
        });
        this.showDelete = false;
        this.$store.commit("SET_SUCCESS", `Deleted gift rule ${data.Gift.name}`);
        if (this.form.id === id) {
          this.resetForm();
        }
        await this.fetchGifts();
      } catch (err) {
        this.$store.commit(
          "SET_ERROR",
          err.response?.data?.errors?.message || err.response?.data?.message || err.message
        );
      }
    },
  },
};
</script>

<style></style>
